import { Form, Typography } from "antd"
import React, { memo, useCallback, useMemo } from "react"

import { type SETTINGS_PERMISSIONS_COLUMNS } from "../constants/columns.ts"
import type { NestedValues } from "../helpers/dependencies.ts"
import type { BoundRow } from "../helpers/section-layout.ts"
import { getColKey } from "../helpers/utils.ts"
import type { ColumnKey, OnBulkChange, SectionRecord } from "../types.ts"
import type { PermissionFeatures } from "../types/permissions.types.ts"
import { PermissionsColumnToggleSwitch } from "./PermissionsColumnToggleSwitch.tsx"

const { Text } = Typography

interface PermissionsColumnToggleRowProps {
  parentKey: keyof PermissionFeatures
  rows: BoundRow[]
  columns: ReadonlyArray<(typeof SETTINGS_PERMISSIONS_COLUMNS)[number]>
  gridTemplateColumns: string
  onBulkChange: OnBulkChange
  isUpdating?: boolean
  disabled?: boolean
}

// A section's rows can be stored under more than one section (Files shows the
// Documents row stored under Projects), so a column switch is applied to each
// stored section in turn.
const groupBySection = (rows: BoundRow[]) =>
  rows.reduce<Map<keyof PermissionFeatures, SectionRecord>>((groups, row) => {
    const group = groups.get(row.parentKey) ?? {}
    group[row.subKey] = row.actions
    groups.set(row.parentKey, group)
    return groups
  }, new Map())

export const PermissionsColumnToggleRow = memo(function ColumnToggleRow({
  parentKey,
  rows,
  columns,
  gridTemplateColumns,
  onBulkChange,
  isUpdating,
  disabled
}: PermissionsColumnToggleRowProps) {
  const watched = Form.useWatch<NestedValues | undefined>([])
  const groups = useMemo(() => groupBySection(rows), [rows])
  const restSubKeys = useMemo(
    () =>
      Object.fromEntries(
        rows.map((row) => [`${row.parentKey}.${row.subKey}`, row.actions])
      ) as SectionRecord,
    [rows]
  )

  const applyToEverySection: OnBulkChange = useCallback(
    (_parentKey, colKey, _subKeys, value) => {
      groups.forEach((subKeys, sectionKey) =>
        onBulkChange(sectionKey, colKey as ColumnKey, subKeys, value)
      )
    },
    [groups, onBulkChange]
  )

  return (
    <div
      className={"py-1.5 px-4 grid items-center border-b border-border"}
      style={{ gridTemplateColumns }}
    >
      <Text type="secondary" className="uppercase text-xs tracking-[0.04em]">
        Toggle column
      </Text>
      {columns.map((col) => {
        const allChecked = rows.every((row) =>
          Object.keys(row.actions)
            .filter((action) => getColKey(action, row.actions) === col.key)
            .every((action) => {
              const watchedVal =
                watched?.[row.parentKey]?.[row.subKey]?.[action]
              return (watchedVal ?? row.actions[action]) === true
            })
        )
        const hasCol = rows.some((row) =>
          Object.keys(row.actions).some(
            (action) => getColKey(action, row.actions) === col.key
          )
        )

        return (
          <PermissionsColumnToggleSwitch
            key={col.key}
            parentKey={parentKey}
            colKey={col.key}
            allChecked={allChecked}
            hasCol={hasCol}
            disabled={disabled}
            restSubKeys={restSubKeys}
            onBulkChange={applyToEverySection}
            isUpdating={isUpdating}
          />
        )
      })}
    </div>
  )
})
